// src/components/tenant/CreateTenantModal.tsx
// Modal for creating new tenants (SuperAdmin only)

import React, { useState, useEffect } from 'react';
import { X, Building2, Mail, Phone, MapPin, Save, AlertCircle, Key, Check, Loader2, AlertTriangle } from 'lucide-react';
import { tenantManagementAPI } from '../../services/tenantManagementAPI';
import { CreateTenantRequest, TenantRecord, OrganizationType, SubscriptionTier } from '../../types/tenant';

interface CreateTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTenantCreated: (tenant: TenantRecord) => void;
}

type SlugValidationState = 'idle' | 'validating' | 'valid' | 'invalid';

const CreateTenantModal: React.FC<CreateTenantModalProps> = ({
  isOpen,
  onClose,
  onTenantCreated
}) => {
  const [formData, setFormData] = useState<CreateTenantRequest>({
    organizationName: '',
    tenantSlug: '',
    organizationType: 'POLICE_DEPARTMENT',
    contactEmail: '',
    contactPhone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    subscriptionTier: 'BASIC',
    maxUsers: 25,
    maxInventoryItems: 5000,
    maxStorageGB: 2,
    features: ['INVENTORY', 'ASSIGNMENTS', 'REPORTS'],
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  
  // Slug-specific states
  const [slugSuggestion, setSlugSuggestion] = useState<string>('');
  const [slugValidationState, setSlugValidationState] = useState<SlugValidationState>('idle');
  const [slugTouched, setSlugTouched] = useState(false);
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);

  const organizationTypes: { value: OrganizationType; label: string }[] = [
    { value: 'POLICE_DEPARTMENT', label: 'Police Department' },
    { value: 'SHERIFF_OFFICE', label: 'Sheriff Office' },
    { value: 'STATE_POLICE', label: 'State Police' },
    { value: 'FEDERAL_AGENCY', label: 'Federal Agency' },
    { value: 'OTHER', label: 'Other' }
  ];

  const subscriptionTiers: { value: SubscriptionTier; label: string; users: number; items: number; storage: number }[] = [
    { value: 'BASIC', label: 'Basic', users: 25, items: 5000, storage: 2 },
    { value: 'PREMIUM', label: 'Premium', users: 100, items: 25000, storage: 10 },
    { value: 'ENTERPRISE', label: 'Enterprise', users: 500, items: 100000, storage: 50 }
  ];

  const availableFeatures = [
    { id: 'INVENTORY', label: 'Inventory Management', description: 'Track and manage equipment' },
    { id: 'ASSIGNMENTS', label: 'Equipment Assignments', description: 'Assign equipment to officers' },
    { id: 'REPORTS', label: 'Reports & Analytics', description: 'Generate reports and analytics' },
    { id: 'USER_MANAGEMENT', label: 'User Management', description: 'Manage department users' },
    { id: 'ONBOARDING', label: 'Officer Onboarding', description: 'Streamlined officer onboarding' },
    { id: 'MOBILE_ACCESS', label: 'Mobile Access', description: 'Mobile app access for officers' }
  ];

  // Auto-generate slug suggestion when organization name changes
  useEffect(() => {
    if (formData.organizationName && formData.organizationName.trim()) {
      const suggestion = tenantManagementAPI.generateSlugSuggestion(formData.organizationName);
      setSlugSuggestion(suggestion);
      
      // If slug field is empty and hasn't been touched, use suggestion
      if (!slugTouched && !formData.tenantSlug) {
        setFormData(prev => ({ ...prev, tenantSlug: suggestion }));
      }
    } else {
      setSlugSuggestion('');
    }
  }, [formData.organizationName, slugTouched]);

  // Validate slug format in real-time
  useEffect(() => {
    const currentSlug = formData.tenantSlug;
    
    if (!currentSlug || currentSlug.trim() === '') {
      setSlugValidationState('idle');
      return;
    }

    // Format validation
    const formatErrors = tenantManagementAPI.validateTenantSlug(currentSlug);
    if (formatErrors.length > 0) {
      setSlugValidationState('invalid');
      setValidationErrors(prev => ({ ...prev, tenantSlug: formatErrors[0] }));
      return;
    }

    // Clear format errors
    setValidationErrors(prev => {
      const { tenantSlug, ...rest } = prev;
      return rest;
    });

    // Check uniqueness with debounce
    setSlugValidationState('validating');
    const timer = setTimeout(() => {
      checkSlugUniqueness(currentSlug);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.tenantSlug]);

  const checkSlugUniqueness = async (slug: string) => {
    if (!slug || slug.trim() === '') return;

    setIsCheckingSlug(true);
    
    try {
      const response = await tenantManagementAPI.getTenantBySlug(slug);
      
      if (response.success) {
        // Slug already exists - tenant was found
        setSlugValidationState('invalid');
        setValidationErrors(prev => ({ 
          ...prev, 
          tenantSlug: 'This department key is already in use. Please choose a different one.' 
        }));
      } else {
        // Slug is available - tenant not found (404)
        setSlugValidationState('valid');
        setValidationErrors(prev => {
          const { tenantSlug, ...rest } = prev;
          return rest;
        });
      }
    } catch (error) {
      // Network error or other unexpected error
      console.error('Error checking slug:', error);
      setSlugValidationState('valid'); // Assume available on error
      setValidationErrors(prev => {
        const { tenantSlug, ...rest } = prev;
        return rest;
      });
    } finally {
      setIsCheckingSlug(false);
    }
  };

  if (!isOpen) return null;

  const validateField = (name: string, value: any): string => {
    switch (name) {
      case 'organizationName':
        return !value?.trim() ? 'Organization name is required' : '';
      case 'contactEmail':
        if (!value?.trim()) return 'Contact email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
        return '';
      case 'maxUsers':
        if (value < 1 || value > 10000) return 'Max users must be between 1 and 10,000';
        return '';
      case 'maxInventoryItems':
        if (value < 1 || value > 1000000) return 'Max inventory items must be between 1 and 1,000,000';
        return '';
      case 'maxStorageGB':
        if (value < 1 || value > 1000) return 'Max storage must be between 1 and 1,000 GB';
        return '';
      default:
        return '';
    }
  };

  const handleInputChange = (name: string, value: any) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSlugChange = (value: string) => {
    setSlugTouched(true);
    handleInputChange('tenantSlug', value);
  };

  const handleSlugBlur = () => {
    // If slug is empty when user leaves field, use suggestion
    if (!formData.tenantSlug && slugSuggestion) {
      setFormData(prev => ({ ...prev, tenantSlug: slugSuggestion }));
    }
  };

  const useSuggestion = () => {
    setFormData(prev => ({ ...prev, tenantSlug: slugSuggestion }));
    setSlugTouched(true);
  };

  const handleSubscriptionTierChange = (tier: SubscriptionTier) => {
    const tierConfig = subscriptionTiers.find(t => t.value === tier);
    if (tierConfig) {
      setFormData(prev => ({
        ...prev,
        subscriptionTier: tier,
        maxUsers: tierConfig.users,
        maxInventoryItems: tierConfig.items,
        maxStorageGB: tierConfig.storage
      }));
    }
  };

  const handleFeatureToggle = (featureId: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features?.includes(featureId)
        ? prev.features.filter(f => f !== featureId)
        : [...(prev.features || []), featureId]
    }));
  };

  const validateForm = (): boolean => {
    const newValidationErrors: { [key: string]: string } = {};
    let isValid = true;

    // Validate all required fields
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key as keyof CreateTenantRequest]);
      if (error) {
        newValidationErrors[key] = error;
        isValid = false;
      }
    });

    // Check slug validation state
    if (formData.tenantSlug && slugValidationState === 'invalid') {
      isValid = false;
      if (!newValidationErrors.tenantSlug) {
        newValidationErrors.tenantSlug = 'Please fix the department key before submitting';
      }
    }

    if (slugValidationState === 'validating') {
      isValid = false;
      newValidationErrors.tenantSlug = 'Please wait while we verify the department key';
    }

    setValidationErrors(newValidationErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      setErrors(['Please fix the validation errors above.']);
      return;
    }

    setLoading(true);
    setErrors([]);

    try {
      // Validate data using API service
      const apiValidationErrors = tenantManagementAPI.validateTenantData(formData);
      if (apiValidationErrors.length > 0) {
        setErrors(apiValidationErrors);
        setLoading(false);
        return;
      }

      const response = await tenantManagementAPI.createTenant(formData);
      
      if (response.success && response.data) {
        onTenantCreated(response.data.tenant);
        onClose();
      } else {
        setErrors([response.error?.message || 'Failed to create tenant']);
      }
    } catch (error: any) {
      setErrors([error.message || 'Failed to create tenant']);
    } finally {
      setLoading(false);
    }
  };

  const getSlugInputIcon = () => {
    if (isCheckingSlug || slugValidationState === 'validating') {
      return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
    }
    if (slugValidationState === 'valid') {
      return <Check className="w-4 h-4 text-green-500" />;
    }
    if (slugValidationState === 'invalid') {
      return <AlertTriangle className="w-4 h-4 text-red-500" />;
    }
    return <Key className="w-4 h-4 text-gray-400" />;
  };

  const getSlugInputBorderColor = () => {
    if (validationErrors.tenantSlug) return 'border-red-300 focus:border-red-500 focus:ring-red-500';
    if (slugValidationState === 'valid') return 'border-green-300 focus:border-green-500 focus:ring-green-500';
    if (slugValidationState === 'validating') return 'border-blue-300 focus:border-blue-500 focus:ring-blue-500';
    return 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Building2 className="w-5 h-5 mr-2 text-blue-600" />
                Create New Tenant
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white px-6 py-4">
            {/* Error Display */}
            {errors.length > 0 && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 mr-3" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800">Please fix the following errors:</h4>
                    <ul className="mt-1 text-sm text-red-700 list-disc list-inside">
                      {errors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {/* Organization Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Organization Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      value={formData.organizationName}
                      onChange={(e) => handleInputChange('organizationName', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        validationErrors.organizationName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="e.g., Seattle Police Department"
                    />
                    {validationErrors.organizationName && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.organizationName}</p>
                    )}
                  </div>

                  {/* Department Key (Slug) Field */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Department Key
                      <span className="ml-1 text-xs text-gray-500 font-normal">
                        (Optional - auto-generated if not provided)
                      </span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        {getSlugInputIcon()}
                      </div>
                      <input
                        type="text"
                        value={formData.tenantSlug || ''}
                        onChange={(e) => handleSlugChange(e.target.value.toLowerCase())}
                        onBlur={handleSlugBlur}
                        className={`w-full pl-10 pr-3 py-2 border rounded-md ${getSlugInputBorderColor()}`}
                        placeholder="e.g., seattlepolice"
                      />
                    </div>
                    
                    {/* Slug suggestion */}
                    {slugSuggestion && formData.tenantSlug !== slugSuggestion && (
                      <div className="mt-2 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-2">
                        <div className="flex items-center text-sm">
                          <Key className="w-4 h-4 text-blue-600 mr-2" />
                          <span className="text-gray-700">Suggested: </span>
                          <code className="ml-1 text-blue-700 font-mono">{slugSuggestion}</code>
                        </div>
                        <button
                          type="button"
                          onClick={useSuggestion}
                          className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          Use this
                        </button>
                      </div>
                    )}

                    {/* Validation feedback */}
                    {validationErrors.tenantSlug && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {validationErrors.tenantSlug}
                      </p>
                    )}
                    {slugValidationState === 'valid' && !validationErrors.tenantSlug && (
                      <p className="mt-1 text-sm text-green-600 flex items-center">
                        <Check className="w-3 h-3 mr-1" />
                        This department key is available
                      </p>
                    )}
                    {slugValidationState === 'validating' && (
                      <p className="mt-1 text-sm text-blue-600 flex items-center">
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Checking availability...
                      </p>
                    )}

                    <p className="mt-1 text-xs text-gray-500">
                      A unique identifier for login. Only lowercase letters, numbers, and hyphens.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Type
                    </label>
                    <select
                      value={formData.organizationType}
                      onChange={(e) => handleInputChange('organizationType', e.target.value as OrganizationType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      {organizationTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subscription Tier
                    </label>
                    <select
                      value={formData.subscriptionTier}
                      onChange={(e) => handleSubscriptionTierChange(e.target.value as SubscriptionTier)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      {subscriptionTiers.map(tier => (
                        <option key={tier.value} value={tier.value}>
                          {tier.label} ({tier.users} users, {tier.items.toLocaleString()} items)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Email *
                    </label>
                    <input
                      type="email"
                      value={formData.contactEmail}
                      onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        validationErrors.contactEmail ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="admin@department.gov"
                    />
                    {validationErrors.contactEmail && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.contactEmail}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      value={formData.contactPhone || ''}
                      onChange={(e) => handleInputChange('contactPhone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <input
                      type="text"
                      value={formData.address || ''}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="123 Main Street"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={formData.city || ''}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Seattle"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      value={formData.state || ''}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      placeholder="WA"
                    />
                  </div>
                </div>
              </div>

              {/* System Limits */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">System Limits</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Users
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10000"
                      value={formData.maxUsers}
                      onChange={(e) => handleInputChange('maxUsers', parseInt(e.target.value))}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        validationErrors.maxUsers ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {validationErrors.maxUsers && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.maxUsers}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Inventory Items
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000000"
                      value={formData.maxInventoryItems}
                      onChange={(e) => handleInputChange('maxInventoryItems', parseInt(e.target.value))}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        validationErrors.maxInventoryItems ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {validationErrors.maxInventoryItems && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.maxInventoryItems}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Storage (GB)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="1000"
                      value={formData.maxStorageGB}
                      onChange={(e) => handleInputChange('maxStorageGB', parseInt(e.target.value))}
                      className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                        validationErrors.maxStorageGB ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {validationErrors.maxStorageGB && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.maxStorageGB}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Available Features</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {availableFeatures.map(feature => (
                    <label key={feature.id} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.features?.includes(feature.id) || false}
                        onChange={() => handleFeatureToggle(feature.id)}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900">{feature.label}</p>
                        <p className="text-xs text-gray-500">{feature.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes
                </label>
                <textarea
                  rows={3}
                  value={formData.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Additional notes about this tenant..."
                />
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || slugValidationState === 'validating' || slugValidationState === 'invalid'}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Tenant
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateTenantModal;
